import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { FilterExpression } from '@/types/query';

type FilterChipProps = {
  filter: FilterExpression;
  onRemove: () => void;
};

function formatValue(filter: FilterExpression, t: TFunction): string {
  const { operator, value } = filter;
  if (operator === 'is_null') return t('filters.op.isNull');
  if (operator === 'is_not_null') return t('filters.op.isNotNull');
  if (operator === 'not_in') {
    return t('filters.chip.notIn', {
      values: (value as Array<string | number>).join(', '),
    });
  }
  if (operator === 'between') {
    const [lo, hi] = value as [string | number, string | number];
    return t('filters.chip.between', { lo, hi });
  }
  if (operator === 'contains') return t('filters.chip.contains', { value });
  if (operator === 'not_contains') {
    return t('filters.chip.notContains', { value });
  }
  return `${t(`filters.op.${operator}`)} ${String(value)}`;
}

function renderBody(filter: FilterExpression, t: TFunction): string {
  if (filter.operator === 'in') {
    const count = Array.isArray(filter.value) ? filter.value.length : 0;
    return t('filters.chip.countIn', { column: filter.column, count });
  }
  return `${filter.column} ${formatValue(filter, t)}`;
}

export function FilterChip({ filter, onRemove }: FilterChipProps): JSX.Element {
  const { t } = useTranslation();
  const isDrilldown = filter.source === 'drilldown';

  return (
    <span
      className="inline-flex items-center gap-1 rounded text-[11px] font-medium max-w-[240px]"
      style={{
        padding: '2px 8px',
        background: isDrilldown
          ? 'var(--color-accent)'
          : 'var(--color-accent-subtle)',
        color: isDrilldown ? '#ffffff' : 'var(--color-accent)',
        border: isDrilldown
          ? 'none'
          : '1px solid var(--color-accent)',
      }}
    >
      <span className="truncate">{renderBody(filter, t)}</span>
      <button
        type="button"
        onClick={onRemove}
        className="ml-0.5 hover:opacity-70 flex-shrink-0"
        aria-label={t('filters.chip.removeAria', { column: filter.column })}
        style={{ lineHeight: 1, color: 'inherit', fontSize: 14 }}
      >
        &times;
      </button>
    </span>
  );
}
